/* eslint-disable indent */
import React, { FC, useEffect, useState } from 'react';

const SIZE = 20;
const INITIAL_SPEED = 200; // Move snake every 200ms
const CELL_SIZE = 20; // Size of each cell in pixels

const INITIAL_HEAD = { x: Math.floor(SIZE / 2), y: Math.floor(SIZE / 2) };
const INITIAL_SNAKE = [
    INITIAL_HEAD,
    { x: INITIAL_HEAD.x - 1, y: INITIAL_HEAD.y },
    { x: INITIAL_HEAD.x - 2, y: INITIAL_HEAD.y },
];
const INITIAL_BOARD = Array.from({ length: SIZE }, () =>
    Array.from({ length: SIZE }, () => ({ snake: 0, food: 0 }))
);
INITIAL_SNAKE.forEach((segment) => {
    INITIAL_BOARD[segment.y][segment.x].snake = 1;
});

export const Snake: FC = () => {
    const [board, setBoard] = useState(INITIAL_BOARD);
    const [direction, setDirection] = useState({ x: 1, y: 0 });
    const [snake, setSnake] = useState(INITIAL_SNAKE);
    const [started, setStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [foodPosition, setFoodPosition] = useState({ x: 5, y: 5 });
    const [speed, setSpeed] = useState(INITIAL_SPEED);

    const startGame = () => {
        setBoard(INITIAL_BOARD);
        setSnake(INITIAL_SNAKE);
        setDirection({ x: 1, y: 0 });
        setFoodPosition({ x: 5, y: 5 });
        setSpeed(INITIAL_SPEED);
        setStarted(false);
        setGameOver(false);
        setStarted(true);
    };

    // Handle keyboard input to change the snake's direction
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowUp':
                    setDirection({ x: 0, y: -1 });
                    break;
                case 'ArrowDown':
                    setDirection({ x: 0, y: 1 });
                    break;
                case 'ArrowLeft':
                    setDirection({ x: -1, y: 0 });
                    break;
                case 'ArrowRight':
                    setDirection({ x: 1, y: 0 });
                    break;
                case 'Enter':
                    if (gameOver) {
                        startGame();
                    }
                    break;
                default:
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameOver]);

    // Render loop: move snake and update board every interval
    useEffect(() => {
        if (!started) return;
        const interval = setInterval(() => {
            setBoard((prevBoard) => {
                const newBoard = Array.from({ length: SIZE }, () =>
                    Array.from({ length: SIZE }, () => ({ snake: 0, food: 0 }))
                );
                const head = { ...snake[0] };
                head.x += direction.x;
                head.y += direction.y;

                // Move the snake
                setSnake((prevSnake) => [head, ...prevSnake.slice(0, -1)]);

                // Update the board with the new snake position
                snake.forEach((segment) => {
                    newBoard[segment.y][segment.x].snake = 1;
                });
                newBoard[foodPosition.y][foodPosition.x].food = 1;

                if (head.x === foodPosition.x && head.y === foodPosition.y) {
                    // Eat food: grow snake and place new food
                    setSnake((prevSnake) => [
                        ...prevSnake,
                        prevSnake[prevSnake.length - 1],
                    ]);
                    const newFoodPosition = {
                        x: Math.floor(Math.random() * SIZE),
                        y: Math.floor(Math.random() * SIZE),
                    };
                    setFoodPosition(newFoodPosition);
                    // Increase speed every food eaten
                    setSpeed((prevSpeed) => Math.max(50, prevSpeed - 10));
                }

                // On wall collision, game over
                if (
                    head.x < 0 ||
                    head.x >= SIZE ||
                    head.y < 0 ||
                    head.y >= SIZE
                ) {
                    setStarted(false);
                    setGameOver(true);
                    clearInterval(interval);
                }

                // On self collision, game over
                if (
                    snake.some(
                        (segment) =>
                            segment.x === head.x && segment.y === head.y
                    )
                ) {
                    setStarted(false);
                    setGameOver(true);
                    clearInterval(interval);
                }
                return newBoard;
            });
        }, speed);
        return () => clearInterval(interval);
    }, [direction, foodPosition.x, foodPosition.y, snake, started, speed]);

    return (
        <div>
            <div style={{ border: '1px solid black', display: 'inline-block' }}>
                {board.map((row, i) => (
                    <div key={i} style={{ display: 'flex' }}>
                        {row.map((cell, j) => (
                            <div
                                key={j}
                                style={{
                                    width: CELL_SIZE,
                                    height: CELL_SIZE,
                                    backgroundColor: cell.snake
                                        ? 'black'
                                        : cell.food
                                          ? 'red'
                                          : 'white',
                                }}
                            />
                        ))}
                    </div>
                ))}
            </div>
            <div>
                <button onClick={startGame}>Start</button>
                {gameOver && <div>Game Over!</div>}
            </div>
        </div>
    );
};
